import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Paper, styled, Tooltip, Typography } from '@mui/material'

import { Box } from '../Box'

export interface InfoRowProps {
  label: string
  /** Rendered as an em dash when empty, so a gap in the record stays visible. */
  value: string
  tooltip?: string
  /**
   * Puts the value under the label rather than opposite it. For columns too
   * narrow to hold both on one line, where the side-by-side form would wrap
   * mid-value.
   */
  stacked?: boolean
}

/**
 * A stacked value can be an unbroken run — a UUID, a long email — with no space
 * for the browser to wrap at, so it is told where it may break. The side-by-side
 * form has the whole row to itself and needs no such help.
 */
const StackedValue = styled(Typography)({
  overflowWrap: 'anywhere',
})

export const InfoRow = ({ label, value, tooltip, stacked }: InfoRowProps) => {
  const labelContent = (
    <Box variant='flex' gap='xs'>
      <Typography variant='body2' color='text.secondary'>
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} placement='top'>
          <InfoOutlinedIcon style={{ fontSize: 14, color: 'inherit', opacity: 0.5, cursor: 'help' }} />
        </Tooltip>
      )}
    </Box>
  )

  if (stacked) {
    return (
      <Paper variant='infoRowStacked'>
        <Box variant='flexColumn' gap='xs'>
          {labelContent}
          <StackedValue variant='body2' fontWeight={500}>
            {value || '—'}
          </StackedValue>
        </Box>
      </Paper>
    )
  }

  return (
    <Paper variant='infoRow'>
      <Box variant='flexBetween'>
        {labelContent}
        <Typography variant='body2' fontWeight={500}>
          {value || '—'}
        </Typography>
      </Box>
    </Paper>
  )
}
